import axios from 'axios';
import { v4 as uuid } from 'uuid';
import { getRequestIdentityHeader } from '@meitu/xdesign-identity';
import { getCountryCode } from '@meitu/xdesign-utils';
import message from '@/ComUI/message';
import { getConfig } from '@/configProvider';
import { ENV_ENUM, getApiHost } from '@/utils/env';
import { getClientLanguage } from '.';
import { getLocaleStore } from '../i18n';
import { getWPSAccessToken } from '../wps/utils';

declare module 'axios' {
    interface AxiosRequestConfig {
        hiddenMessage?: boolean;
        directReturn?: boolean;
    }
}

export const getSource = async (): Promise<string> => {
    const { clientInfo } = (await import('@meitu/pc-bridge')).default;
    if (clientInfo.isMac || clientInfo.isMacAppStore) {
        return 'MTXX-MAC';
    } else if (clientInfo.isPC) {
        return 'MTXX-PC';
    } else if (clientInfo.isLinux) {
        return 'MTXX-LINUX';
    } else if (clientInfo.isHarmony) {
        return 'MTXX-HARMONY';
    } else {
        return 'MTXX-WEB';
    }
};

export const getCommonParams = async () => {
    return {
        gid: getConfig().gid,
        education_role: getConfig().userInfo?.education_role ?? '',
        client_id: getConfig().client_id,
        preview_mode: getConfig().env === ENV_ENUM.release ? 0 : 1,
        client_language: getClientLanguage(getLocaleStore().locale),
        country_code: await getCountryCode(),
        ts_random_id: uuid(),
        client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        channel: getConfig().channel,
        source: await getSource(),
    };
};

const request = axios.create({
    withCredentials: true,
});

const getDefaultError = () => {
    const { $t } = getLocaleStore();
    return {
        code: 1024,
        message: $t((v) => v.utils.requestErrorText),
    };
};

export function getAccessToken() {
    const { isWpsUser } = getConfig();

    if (isWpsUser) {
        const wpsAccessToken = getWPSAccessToken();

        return wpsAccessToken || '';
    }

    if (typeof window !== 'undefined') {
        return window.localStorage.getItem('access_token') || '';
    }
    return '';
}

/** 用户被团队移除的状态码 */
export const RESIGNED_CODE = 402;

request.interceptors.request.use(
    async (config) => {
        config.baseURL = config.baseURL ?? getApiHost();
        const commonParams = await getCommonParams();
        const customHeader =
            typeof window === 'undefined'
                ? {}
                : {
                      access_token: getAccessToken(),
                      ...getRequestIdentityHeader(),
                  };

        config.headers = {
            ...customHeader,
            ...config.headers.common,
            ...config.headers,
        };
        config.params = {
            ...commonParams,
            ...config.params,
        };
        return config;
    },
    () => Promise.reject(getDefaultError()),
);

request.interceptors.response.use(
    async (response) => {
        // 直接返回数据
        if (response.config?.directReturn) {
            return response.data;
        }
        const code = response.data.code;
        const error_code = response.data.error_code;
        const messageText = response.data.message || getDefaultError().message;
        if (code) {
            !response?.config?.hiddenMessage && message.error(messageText);
            throw { code, message: messageText, error_code };
        }
        return response.data?.data;
    },
    (error) => {
        const data = error?.response?.data || getDefaultError();
        const code = data.code;
        const error_code = data.error_code;
        const messageText = data.message;
        if (!error?.config?.hiddenMessage) {
            message.error(messageText);
        }

        return Promise.reject({ code, message: messageText, error_code });
    },
);

export default request;
